import axios from "axios";
import { useCallback, useState } from "react";
import { useAsync } from "react-use";
import ask from "Dialogs/ask";
import showMessage from "Dialogs/showMessage";
import { CollectionSession, CollectionSessionElement, SessionDocumentType } from "system/Domain";

export interface AddVehicleRequest {
	vin?: string;
	plateNumber?: string;
	pickupToken?: string;
}

/**
 * Loads a CollectionSession (bulk shipment-form session) and its currently active vehicles, and
 * exposes the mutation actions (add/remove vehicle, cancel) shared by the session's detail and
 * scan pages.
 */
const useCollectionSession = (id: string) => {
	const [session, setSession] = useState<CollectionSession | null>();
	const [elements, setElements] = useState<CollectionSessionElement[] | null>();
	const [isLoading, setIsLoading] = useState(false);

	const loadElements = useCallback(async () => {
		const { data } = await axios.get<CollectionSessionElement[]>(`/api/sales/collection-sessions/${id}/elements`);

		setElements(data);
	}, [id]);

	useAsync(async () => {
		const { data } = await axios.get<CollectionSession>(`/api/sales/collection-sessions/${id}`);

		setSession(data);

		await loadElements();
	}, [id]);

	const addVehicle = useCallback(async (
		request: AddVehicleRequest,
		confirmSwitchToDigitalForm = false,
		applySessionDocuments?: boolean
	): Promise<boolean> => {
		setIsLoading(true);

		try {
			const { data } = await axios.post<CollectionSession>(`/api/sales/collection-sessions/${id}/vehicles`, {
				...request,
				confirmSwitchToDigitalForm,
				applySessionDocuments
			});

			setSession(data);
			await loadElements();

			return true;
		} catch (error) {
			if (error.isAxiosError && error.response?.status === 409 && error.response.data === "PaperShipmentFormConfirmationRequired") {
				const { yes } = await ask(
					"Für dieses Fahrzeug ist aktuell das manuelle (Papier-) Ausgangsprotokoll aktiv. Soll es auf digital umgestellt und zur Sammelsitzung hinzugefügt werden?"
				);

				if (yes) {
					return await addVehicle(request, true, applySessionDocuments);
				}

				return false;
			}

			if (error.isAxiosError && error.response?.status === 409 && error.response.data === "SessionDocumentsConfirmationRequired") {
				const { yes } = await ask(
					"Zu dieser Sammelsitzung wurden bereits Dokumente (Frachtpapiere / Andere) hochgeladen. Sollen diese auch am hinzugefügten Fahrzeug hinterlegt werden?"
				);

				return await addVehicle(request, confirmSwitchToDigitalForm, yes);
			}

			if (error.isAxiosError && error.response?.status < 500) {
				await showMessage({ title: "Fehler", message: error.response.data, color: "error" });
				return false;
			}

			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [id, loadElements]);

	const removeVehicle = useCallback(async (saleId: string, removeDocuments?: boolean): Promise<void> => {
		if (removeDocuments === undefined) {
			const { yes } = await ask("Fahrzeug wirklich aus der Sammelsitzung entfernen?");

			if (!yes) return;
		}

		setIsLoading(true);
		try {
			const { data } = await axios.delete<CollectionSession>(`/api/sales/collection-sessions/${id}/vehicles/${saleId}`, {
				params: { removeDocuments }
			});

			setSession(data);
			await loadElements();
		} catch (error) {
			if (error.isAxiosError && error.response?.status === 409 && error.response.data === "SessionDocumentsRemovalConfirmationRequired") {
				setIsLoading(false);

				const { yes } = await ask(
					"An diesem Fahrzeug sind Dokumente aus der Sammelsitzung hinterlegt. Sollen diese Dokumente ebenfalls vom Fahrzeug entfernt werden?"
				);

				return await removeVehicle(saleId, yes);
			}

			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [id, loadElements]);

	const uploadDocument = useCallback(async (documentType: SessionDocumentType, file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("documentType", documentType);

		setIsLoading(true);
		try {
			const { data } = await axios.post<CollectionSession>(
				`/api/sales/collection-sessions/${id}/documents`,
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } }
			);

			setSession(data);
			await loadElements();
		} catch (error) {
			if (error.isAxiosError && error.response?.status < 500) {
				await showMessage({ title: "Fehler", message: error.response.data, color: "error" });
				return;
			}

			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [id, loadElements]);

	const deleteDocument = useCallback(async (hash: string) => {
		const { yes } = await ask("Dokument wirklich aus der Sammelsitzung und von allen Fahrzeugen entfernen?");

		if (!yes) return;

		setIsLoading(true);
		try {
			const { data } = await axios.delete<CollectionSession>(`/api/sales/collection-sessions/${id}/documents/${hash}`);

			setSession(data);
			await loadElements();
		} catch (error) {
			if (error.isAxiosError && error.response?.status < 500) {
				await showMessage({ title: "Fehler", message: error.response.data, color: "error" });
				return;
			}

			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [id, loadElements]);

	const cancelSession = useCallback(async () => {
		const { yes } = await ask("Sammelsitzung wirklich abbrechen? Alle Fahrzeuge werden aus der Sitzung entfernt.");

		if (!yes) return;

		setIsLoading(true);
		try {
			const { data } = await axios.post<CollectionSession>(`/api/sales/collection-sessions/${id}/cancel`);

			setSession(data);
			await loadElements();
		} finally {
			setIsLoading(false);
		}
	}, [id, loadElements]);

	const confirmShipping = useCallback(async () => {
		const { yes } = await ask("Wollen Sie die Auslieferung für alle Fahrzeuge dieser Sitzung bestätigen?");

		if (!yes) return false;

		setIsLoading(true);
		try {
			const { data } = await axios.post<CollectionSession>(`/api/sales/collection-sessions/${id}/confirm-shipping`, {
				shippingDate: new Date().toISOString()
			});

			setSession(data);
			await loadElements();

			return true;
		} catch (error) {
			// each vehicle's shipping confirmation is applied independently on the backend, so even when
			// this request fails for some vehicles, others may have already been confirmed - reload to
			// reflect whatever partially succeeded before showing the error
			await loadElements();

			if (error.isAxiosError && error.response?.status < 500) {
				await showMessage({ title: "Fehler", message: error.response.data, color: "error" });
				return false;
			}

			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [id, loadElements]);

	return {
		session,
		elements,
		isLoading,
		addVehicle,
		removeVehicle,
		cancelSession,
		confirmShipping,
		uploadDocument,
		deleteDocument,
		reload: loadElements
	};
};

export default useCollectionSession;
